import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Grid,
  GridItem,
  Text,
  Image,
  VStack,
  HStack,
  IconButton,
  useColorModeValue,
  Badge,
  Flex,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Textarea,
  useDisclosure,
  useToast,
  Switch,
  Select
} from '@chakra-ui/react';

import { AddIcon, EditIcon, AttachmentIcon, DeleteIcon, LockIcon } from '@chakra-ui/icons';
import SignatureCanvas from 'react-signature-canvas';
import { uploadSignatureImage, lockUserItemPreference, uploadUserEquipmentImage, createUserEquipment, deleteUserEquipment, uploadUserItemPreferenceMaterial } from '../services/api';
import { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useNavigate, useLocation } from 'react-router-dom';
dayjs.extend(utc);
dayjs.extend(timezone);

function UserEquipmentModal({ isOpen, onClose, onRegister, initialData, config, targetUserId, isTsubo }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isFunctionEnabled } = useAuth();
  const generateInitialForm = (fields) => {
    let initial = {};
    if (fields) {
      Object.entries(fields).forEach(([name, field]) => {
        if (field.type === 'number') {
          initial[field.name] = '';
        } else if (field.type === 'boolean') {
          initial[field.name] = field.default || false;
        } else {
          initial[field.name] = '';
        }
      });
    }
    return initial;
  };

  const formFields = config?.fields || {};
  const [form, setForm] = useState(() => generateInitialForm(formFields));
  const [rooms, setRooms] = useState(initialData?.rooms || []);
  const [imageFiles, setImageFiles] = useState([]);
  const fileInputRef = useRef();

  // モーダルが開いたときにフォームを初期化/リセット
  useEffect(() => {
    if (isOpen) {
      setForm(generateInitialForm(formFields));
      setRooms(initialData?.rooms || []);
      setImageFiles([]);
    }
  }, [isOpen, formFields, initialData]);

  // FloorPlannerから戻ってきた場合roomsを反映
  useEffect(() => {
    if (isOpen && location.state?.rooms) {
      setRooms(location.state.rooms);
    }
  }, [isOpen, location.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleNumberChange = (name, value) => {
    if ( value === '' ) {
      setForm(f => f.drop(name));
    } else {
      setForm(f => ({ ...f, [name]: Number(value) }));
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 5 - imageFiles.length);
    setImageFiles(prev => [...prev, ...files]);
    setForm(f => ({ ...f, images: [...f.images, ...files] }));
    fileInputRef.current.value = '';
  };

  const handleImageRemove = (idx) => {
    setImageFiles(prev => prev.filter((_, i) => i !== idx));
    setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const handleEditFloorplan = () => {
    const url = (targetUserId && targetUserId !== auth.currentUser?.uid) ? `/admin/user/${targetUserId}/planner` : '/planner';
    navigate(url, { state: { floorRooms: rooms, targetUserId: targetUserId, mode: 'create' } });
  };

  const handleRegister = () => {
    const data = { ...form };
    for ( const field of config.fields ) {
      if ( field.type === 'number' && data[field.name] === '' ) {
        delete data[field.name];
      }
    }
    if (config.key === 'floorplan') {
      data.rooms = rooms;
    }
    onRegister(data);
  };

  // 動的にフォームフィールドをレンダリングする関数
  const renderFormField = (fieldName, field) => {
    if (field.name === 'recommended' || field.name === 'images') {
      return null;
    }
    if (config.key === 'floorplan' && field.unit === 'm2') {
      return (
        <FormControl key={field.name} isRequired={field.required}>
          <FormLabel>{field.label}{isTsubo ? '（坪）' : '（m²）'}</FormLabel>
          <NumberInput
            min={0}
            value={form[field.name] ?? ''}
            onChange={(valueString) => handleNumberChange(field.name, valueString)}
          >
            <NumberInputField name={field.name} />
          </NumberInput>
        </FormControl>
      );
    }
    switch (field.type) {
      case 'number':
        return (
          <FormControl key={field.name} isRequired={field.required}>
            <FormLabel>{field.label}{field.unit ? ` (${field.unit})` : ''}</FormLabel>
            <NumberInput
              min={0}
              value={form[field.name] ?? ''}
              onChange={(valueString) => handleNumberChange(field.name, valueString)}
            >
              <NumberInputField name={field.name} />
            </NumberInput>
          </FormControl>
        );
      case 'boolean':
        return (
          <FormControl key={field.name} isRequired={field.required}>
            <FormLabel>{field.label}</FormLabel>
            <Switch
              isChecked={form[field.name] ?? field.default ?? false}
              onChange={(e) => setForm(f => ({ ...f, [field.name]: e.target.checked }))}
            />
          </FormControl>
        );
      default:
        return (
          <FormControl key={field.name} isRequired={field.required}>
            <FormLabel>{field.label}</FormLabel>
            <Textarea
              name={field.name}
              value={form[field.name] ?? ''}
              onChange={handleChange}
              placeholder={field.placeholder || ''}
              type={field.inputType || 'text'}
            />
          </FormControl>
        );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>自分で{config.key==='floorplan' ? '間取り' : '設備'}を登録</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3} align="stretch">
            {Object.entries(formFields).map(([name, field]) => renderFormField(name, field))}
           <FormControl>
              <FormLabel>画像（最大5枚）</FormLabel>
              <Input type="file" accept="image/*" multiple ref={fileInputRef} onChange={handleImageChange} isDisabled={imageFiles.length >= 5} />
              <HStack mt={2} spacing={2} wrap="wrap">
                {imageFiles.map((file, idx) => (
                  <Box key={idx} position="relative" width="100%" mb={4}>
                    <Image 
                      src={URL.createObjectURL(file)} 
                      width="100%" 
                      height="auto" 
                      objectFit="contain" 
                      borderRadius="md" 
                    />
                    <IconButton 
                      icon={<DeleteIcon />} 
                      size="sm" 
                      colorScheme="black" 
                      position="absolute" 
                      top={2} 
                      right={2} 
                      aria-label="画像削除" 
                      onClick={() => handleImageRemove(idx)} 
                    />
                  </Box>
                ))}
              </HStack>
            </FormControl>
            {config.key === 'floorplan' && isFunctionEnabled('floor_plan') && (
              <Button bg="black" color="white" onClick={handleEditFloorplan} mb={2}>間取りを編集</Button>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            bg="black"
            color="white"
            _hover={{ bg: 'gray.700' }}
            _active={{ bg: 'gray.800' }}
            mr={3}
            onClick={handleRegister}
          >
            登録
          </Button>
          <Button variant="outline" colorScheme="gray" onClick={onClose}>
            キャンセル
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function SignaturePadModal({ isOpen, onClose, onSave }) {
  const sigPadRef = useRef();
  const [error, setError] = useState('');

  const handleClear = () => {
    sigPadRef.current.clear();
    setError('');
  };
  const handleSave = () => {
    if (sigPadRef.current.isEmpty()) {
      setError('サインを入力してください');
      return;
    }
    sigPadRef.current.getCanvas().toBlob(blob => {
      onSave(blob);
    });
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>サインしてロック</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3}>
            <SignatureCanvas
              ref={sigPadRef}
              penColor="black"
              backgroundColor="#fff"
              canvasProps={{ width: 400, height: 200, style: { border: '1px solid #ccc', borderRadius: 8 } }}
            />
            {error && <Text color="black">{error}</Text>}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleClear} mr={3}>クリア</Button>
          <Button bg="black" color="white" onClick={handleSave}>サインしてロック</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function MemoModal({ isOpen, onClose, memo, onSave, isLocked, preferenceId, materialFiles = [], onUploadMaterial, onReloadMaterialFiles, onPreferenceChange, itemType, itemId }) {
  const [value, setValue] = useState(memo || '');
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState(materialFiles);
  useEffect(() => {
    if (isOpen) {
      setValue(memo || '');
      setFiles(materialFiles || []);
    }
  }, [isOpen, materialFiles]);

  // ファイルアップロード処理
  const handleFileChange = async (e) => {
    if (!e.target.files || !e.target.files[0]) return;
    setUploading(true);
    try {
      await onUploadMaterial(preferenceId, e.target.files[0]);
      if (onReloadMaterialFiles) await onReloadMaterialFiles();
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // ファイル削除処理
  const handleFileDelete = async (idx) => {
    if (isLocked) return;
    if (!window.confirm('本当にこの資料を削除してもよいですか？')) return;
    const newFiles = files.filter((_, i) => i !== idx);
    setFiles(newFiles);

    // 即時API反映
    if (preferenceId && onPreferenceChange) {
      await onPreferenceChange(itemId, itemType, { material_files: newFiles });
      if (onReloadMaterialFiles) await onReloadMaterialFiles();
    }
  };

  // ファイルの種類判定
  const isImage = (url) => /\.(jpg|jpeg|png|gif|bmp)(\?.*)?$/i.test(url);
  const isPdf = (url) => /\.pdf(\?.*)?$/i.test(url);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>メモ・資料添付</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Textarea
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder="この設備に関するメモを入力..."
            isReadOnly={isLocked}
            minH="120px"
          />
          <Box mt={4}>
            <Text fontWeight="bold" mb={2}>資料ファイル添付</Text>
            <Input type="file" accept="image/*,.pdf" onChange={handleFileChange} isDisabled={isLocked || uploading} mb={2} />
            <VStack align="start" spacing={2} mt={2} maxH="1000px" overflowY="auto">
              {files && files.length > 0 ? files.map((url, idx) => (
                <Box key={idx} borderWidth="1px" borderRadius="md" p={2} w="100%" bg="gray.50" position="relative">
                  {isImage(url) ? (
                    <Box>
                      <Text fontSize="sm" color="gray.600">{`資料${idx+1}`}</Text>
                      <Image src={url} alt={`資料${idx+1}`} maxWidth="100%" maxHeight="300px" objectFit="cover" display="inline-block" mr={2} />
                    </Box>
                  ) : isPdf(url) ? (
                    <Box>
                      <Text fontSize="sm" color="gray.600">{`資料${idx+1}`}</Text>
                      <a href={url} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" colorScheme="teal" variant="outline">PDFを開く/ダウンロード</Button>
                      </a>
                    </Box>
                  ) : (
                    <Box>
                      <a href={url} target="_blank" rel="noopener noreferrer">ファイルを開く</a>
                    </Box>
                  )}
                  {/* 削除ボタン */}
                  {!isLocked && (
                    <IconButton
                      icon={<DeleteIcon />}
                      size="xs"
                      colorScheme="red"
                      aria-label="添付ファイル削除"
                      position="absolute"
                      top={1}
                      right={1}
                      onClick={() => handleFileDelete(idx)}
                    />
                  )}
                </Box>
              )) : (
                <Text color="gray.400" fontSize="sm">添付ファイルはありません</Text>
              )}
            </VStack>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} mr={3}>閉じる</Button>
          <Button bg="black" color="white" onClick={() => onSave(value, files)} isDisabled={isLocked}>保存</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default function EquipmentList({
  items,
  config,
  onPreferenceChange,
  onItemSelect,
  isFixedList = false,
  onReloadPreferences,
  onReloadMaterialFiles,
  user_item_preferences,
  onReloadUserEquipments,
  targetUserId,
  isTsubo = false
}) {
  const { isOpen: isUserModalOpen, onOpen: onUserModalOpen, onClose: onUserModalClose } = useDisclosure();
  const toast = useToast();
  const { isFunctionEnabled } = useAuth();

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const subTextColor = useColorModeValue('gray.600', 'gray.400');
  const fixedBg = useColorModeValue('gray.50', 'gray.900');
  const tileBg = useColorModeValue('white', 'gray.800');
  const tileBorder = useColorModeValue('gray.200', 'gray.600');

  const isAdmin = targetUserId != auth.currentUser?.uid;

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '価格未定';
    return `¥${price.toLocaleString()}`;
  };

  // 画像URLの取得
  const getImageUrl = (item) => {
    // 新しい形式（images配列）をチェック
    if (item.images && item.images.length > 0) {
      return item.images[0];
    }
    // 古い形式（個別のURL）をチェック
    if (item.image1_sas_url) {
      return item.image1_sas_url;
    }
    return null;
  };

  // ユーザー登録処理
  const handleRegisterEquipment = async (formData) => {
    const user = auth.currentUser;
    if (!user) {
      toast({
        title: 'エラー',
        description: 'ログインが必要です',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // 画像ファイルのアップロード
      const imageUrls = [];
      for (const imageFile of formData.images) {
        const imageData = await uploadUserEquipmentImage(config.key, imageFile, user, targetUserId);
        imageUrls.push(imageData.url);
      }

      // 設備データの登録
      const equipmentData = {
        ...formData,
        images: imageUrls,
        user_id: targetUserId,
        item_type: config.key
      };

      await createUserEquipment(config.key, equipmentData);

      toast({
        title: '登録完了',
        description: '設備を登録しました',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      if (onReloadUserEquipments) {
        await onReloadUserEquipments();
      }
    } catch (error) {
      toast({
        title: 'エラー',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const [lockTarget, setLockTarget] = useState(null); // {item, idx}
  const [isSigModalOpen, setSigModalOpen] = useState(false);
  const [loadingLock, setLoadingLock] = useState(false);

  // サインパッドモーダルを開く
  const openLockModal = (item) => {
    setLockTarget(item);
    setSigModalOpen(true);
  };
  const closeLockModal = () => {
    setLockTarget(null);
    setSigModalOpen(false);
  };

  // サイン画像アップロード＆ロックAPI呼び出し
  const handleLock = async (signatureBlob) => {
    if (!lockTarget) return;
    setLoadingLock(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('未ログインです');
      // サイン画像アップロード
      const uploadRes = await uploadSignatureImage(user.uid, lockTarget.item_type, signatureBlob);
      const signatureUrl = uploadRes.url;
      // ロック情報をUserItemPreferenceにUPSERT
      const now = new Date().toISOString();
      await lockUserItemPreference({
        item_type: lockTarget.item_type,
        item_id: lockTarget.id,
        is_liked: lockTarget.is_liked,
        is_fixed: lockTarget.is_fixed,
        locked_at: now,
        signature_url: signatureUrl
      });
      toast({ status: 'success', title: 'ロックしました' });
      if (onReloadPreferences) {
        await onReloadPreferences();
      }
      closeLockModal();
    } catch (e) {
      toast({ status: 'error', title: 'ロックに失敗しました', description: e.message });
    } finally {
      setLoadingLock(false);
    }
  };

  const [memoTarget, setMemoTarget] = useState(null); // {item}
  const [isMemoModalOpen, setMemoModalOpen] = useState(false);

  const openMemoModal = async (item) => {
    const user = auth.currentUser;
    if (!user) throw new Error('未ログインです');
    const pref = user_item_preferences?.find(
      p => p.item_type === item.item_type && p.item_id === item.id
    );
    setMemoTarget({
      ...item,
      ...pref,
      item_id: item.id,
      material_files: pref?.material_files || []  // 初期値を空配列に
    });
    setMemoModalOpen(true);
  };
  const closeMemoModal = async () => {
    setMemoTarget(null);
    setMemoModalOpen(false);
    if (onReloadPreferences) {
      await onReloadPreferences();
    }
  };
  const handleMemoSave = async (newMemo, newFiles) => {
    if (!memoTarget) return;
    await onPreferenceChange(memoTarget.item_id, memoTarget.item_type, { memo: newMemo, material_files: newFiles });
    if (onReloadPreferences) {
      await onReloadPreferences();
    }
    closeMemoModal();
  };

  useEffect(() => {
    if (isMemoModalOpen && memoTarget) {
      const pref = user_item_preferences?.find(
        p => p.item_type === memoTarget.item_type && p.item_id === memoTarget.item_id
      );
      setMemoTarget(prev => ({
        ...prev,
        ...pref,
        material_files: pref?.material_files || []  // 初期値を空配列に
      }));
    }
  }, [user_item_preferences, isMemoModalOpen]);

  const [unlockTarget, setUnlockTarget] = useState(null);
  const [isUnlockModalOpen, setUnlockModalOpen] = useState(false);
  const [loadingUnlock, setLoadingUnlock] = useState(false);

  // ロック解除確認モーダル
  const openUnlockModal = (item) => {
    setUnlockTarget(item);
    setUnlockModalOpen(true);
  };
  const closeUnlockModal = () => {
    setUnlockTarget(null);
    setUnlockModalOpen(false);
  };
  const handleUnlock = async () => {
    if (!unlockTarget) return;
    setLoadingUnlock(true);
    try {
      await onPreferenceChange(unlockTarget.id, unlockTarget.item_type, { locked_at: null, signature_url: null });
      toast({ status: 'success', title: 'ロックを解除しました' });
      if (onReloadPreferences) await onReloadPreferences();
      closeUnlockModal();
    } catch (e) {
      toast({ status: 'error', title: 'ロック解除に失敗しました', description: e.message });
    } finally {
      setLoadingUnlock(false);
    }
  };

  const [selectedLocations, setselectedLocations] = useState([]);
  const handleLocationChange = (e, item, index) => {
    setselectedLocations(prev => {
      const newLocations = [...prev];
      newLocations[index] = e.target.value;
      return newLocations;
    });
  };
  const handleLocationDelete = (item, index) => {
    const quantity = selectedLocations.length;
    setselectedLocations(prev => prev.filter((_, i) => i !== index));
    onPreferenceChange(item.id, item.item_type, { quantity: quantity - 1 })
  };
  const handleLocationAdd = (item) => {
    const quantity = selectedLocations.length;
    setselectedLocations(prev => [...prev, '']);
    onPreferenceChange(item.id, item.item_type, { quantity: quantity + 1 })
  };
  return (
    <>
    <Grid
      templateColumns={{
        base: 'repeat(1, 1fr)',
        sm: 'repeat(2, 1fr)',
        md: 'repeat(3, 1fr)',
        lg: 'repeat(4, 1fr)'
      }}
      gap={4}
    >
      {!isFixedList && (
        <GridItem>
        <Box
          borderWidth="1px"
          borderColor={tileBorder}
          borderRadius="md"
          p={4}
          bg={tileBg}
          boxShadow="sm"
          transition="background 0.2s, box-shadow 0.2s"
          cursor="pointer" // _hover={{ bg: useColorModeValue('blue.50', 'blue.900'), boxShadow: 'md' }}
          _hover={{ bg: useColorModeValue('gray.100', 'gray.700'), boxShadow: 'md' }}
          onClick={onUserModalOpen}
          display="flex"
          alignItems="center"
          justifyContent="center"
          h="100%"
        >
          <VStack spacing={2}>
            <Box
              w="70px"
              h="70px"
              borderRadius="md"
              bg={useColorModeValue('gray.200', 'gray.600')}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <AddIcon boxSize="24px" color={useColorModeValue('black', 'white')} />
            </Box>
            <Text color={useColorModeValue('black', 'white')} fontWeight="bold">
              自分で{config.key==='floorplan' ? '間取り' : '設備'}を登録
            </Text>
          </VStack>
        </Box>
        </GridItem>
      )}

      {items.map(item => {
        const preference = user_item_preferences?.find(
          p => p.item_type === item.item_type && p.item_id === item.id
        );
        return (
        <GridItem key={item.id}>
          <Box
            bg={isFixedList ? fixedBg : cardBg}
            borderWidth="1px"
            borderColor={cardBorder}
            borderRadius="lg"
            overflow="hidden"
            position="relative"
            transition="transform 0.2s"
            _hover={{ transform: 'translateY(-2px)' }}
          >
            <Box position="relative">
              {getImageUrl(item) ? (
                <Image
                  src={getImageUrl(item)}
                  alt={item.name}
                  w="100%"
                  h="200px"
                  objectFit="contain"
                    onClick={() => onItemSelect(item.id, item.is_user_equipment)}
                  cursor="pointer"
                />
              ) : (
                <Flex
                  w="100%"
                  h="200px"
                  bg="gray.100"
                  align="center"
                  justify="center"
                  color="gray.500"
                    onClick={() => onItemSelect(item.id, item.is_user_equipment)}
                  cursor="pointer"
                >
                  画像なし
                </Flex>
              )}
              <HStack
                position="absolute"
                top={2}
                right={2}
                spacing={1}
              >
                <Button
                  size="sm"
                  colorScheme="pink"
                  bg="white"
                  onClick={() => onPreferenceChange(item.id, item.item_type, { is_liked: !item.is_liked })}
                    isDisabled={isFixedList ? !!item.locked_at : false}
                >
                  {item.is_liked ? '❤️' : '🤍'}
                </Button>
                  {!item.locked_at && (
                  <Tooltip label={item.is_fixed ? "暫定を解除" : "暫定"}>
                    <Button
                      size="sm"
                      colorScheme={item.is_fixed ? "yellow" : "gray"}
                      variant={item.is_fixed ? "solid" : "outline"}
                      bg={item.is_fixed ? "yellow.400" : "white"}
                      onClick={() => onPreferenceChange(item.id, item.item_type, { is_fixed: !item.is_fixed })}
                    >
                      {item.is_fixed ? '★' : '☆'}
                    </Button>
                  </Tooltip>
                  )}
                  {item.is_fixed && !item.locked_at && !isAdmin && isFunctionEnabled('lock') && (
                    <Tooltip label="ロック（以後編集不可）">
                      <IconButton
                        icon={<LockIcon />}                    
                        size="sm"
                        colorScheme="gray"
                        variant="outline"
                        bg="white"
                        onClick={() => openLockModal(item)}
                        isLoading={loadingLock && lockTarget?.id === item.id}
                      />
                    </Tooltip>
                  )}
                  {item.is_fixed && item.locked_at && isAdmin && isFunctionEnabled('lock') && (
                    <Tooltip label="ロック解除">
                      <IconButton
                        icon={<LockIcon />}                    
                        size="sm"
                        colorScheme="yellow"
                        variant="outline"
                        onClick={() => openUnlockModal(item)}
                        isLoading={loadingUnlock && unlockTarget?.id === item.id}
                      />
                    </Tooltip>
                  )}
                  {item.locked_at && !isAdmin && isFunctionEnabled('lock') && (
                    <Tooltip label="ロック済み">
                      <IconButton icon={<LockIcon />} size="sm" colorScheme="yellow" variant="ghost" isDisabled ml={1} />
                    </Tooltip>                    
                  )}
                  <IconButton
                    size="sm"
                    icon={<EditIcon />}
                    colorScheme={(item.memo || (preference?.material_files?.length > 0)) ? "yellow" : "gray"}
                    aria-label="メモ"
                    onClick={() => openMemoModal(item)}
                    isRound
                  />
                  {item.is_user_equipment && !item.locked_at && (
                    <IconButton
                      size="sm"
                      icon={<DeleteIcon />}
                      colorScheme="yellow"
                      aria-label="削除"
                      onClick={async () => {
                        if (!window.confirm('本当にこの設備を削除してもよいですか？')) return;
                        try {
                          const user = auth.currentUser;
                          if (!user) throw new Error('未ログインです');
                          await deleteUserEquipment(item.item_type, item.id, targetUserId);
                          toast({
                            title: "アイテムを削除しました",
                            status: "success",
                            duration: 3000,
                            isClosable: true,
                          });
                          // 親コンポーネントに再読み込みを通知
                          if (onReloadUserEquipments) {
                            await onReloadUserEquipments();
                          }
                        } catch (error) {
                          toast({
                            title: "エラー",
                            description: error.message,
                            status: "error",
                            duration: 5000,
                            isClosable: true,
                          });
                        }
                      }}
                      isRound
                    />
                  )}
              </HStack>
            </Box>

            <VStack p={4} align="stretch" spacing={2}>
              <Flex justify="space-between" align="center">
                <Text fontSize="sm" color={subTextColor}>
                  {item.manufacturer}
                </Text>
                  {item.is_user_equipment ? (
                    <Badge colorScheme="black">ユーザー登録</Badge>
                  ) : item.recommended && (
                  <Badge colorScheme="black">おすすめ</Badge>
                )}
              </Flex>
              <Text
                fontSize="md"
                fontWeight="bold"
                color={textColor}
                noOfLines={2}
                  onClick={() => onItemSelect(item.id, item.is_user_equipment)}
                cursor="pointer"
              >
                {item.name}
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="black">
                {formatPrice(item.price)}
              </Text>
              <Text fontSize="sm" color={subTextColor} noOfLines={2}>
                {item.features}
              </Text>

              { !isFixedList && config.fields?.map(field => {
                const value = item[field.name];
                if (value === undefined || value === null || 
                    field.name === 'price' || field.name === 'features' || 
                    field.name === 'recommended' || field.name === 'manufacturer' || 
                    field.name === 'name' || field.name === 'images' ||
                    (item.is_user_equipment && field.name === 'user_id')) return null;
                // floorplanのm2のみ単位切替
                if (config.key === 'floorplan' && field.unit === 'm2') {
                  return (
                    <Text key={field.name} fontSize="sm" color={subTextColor}>
                      {field.label}: {isTsubo ? (value ? (Number(value) / 3.305785).toFixed(2) + '坪' : '') : value + 'm²'}
                    </Text>
                  );
                }
                return (
                  <Text key={field.name} fontSize="sm" color={subTextColor}>
                    {field.label}: {typeof value === 'boolean' ? (value ? 'あり' : 'なし') : value}{field.unit || ''}
                  </Text>
                );
              })}
            </VStack>

            {isFixedList && (
                <Flex align="center" mt={2} mb={2} gap={2}>
                  <Text fontSize="sm" color="gray.700" fontWeight="bold">数量:</Text>
                  <Button
                    size="xs"
                    onClick={() => onPreferenceChange(item.id, item.item_type, { quantity: Math.max(1, (item.quantity || 1) - 1) })}
                    isDisabled={isFunctionEnabled('lock') ? !!item.locked_at || (item.quantity || 1) <= 1 : (item.quantity || 1) <= 1}
                    aria-label="数量を減らす"
                  >
                    -
                  </Button>
                  <Text fontSize="md" w="32px" textAlign="center">{item.quantity || 1}</Text>
                  <Button
                    size="xs"
                    onClick={() => onPreferenceChange(item.id, item.item_type, { quantity: (item.quantity || 1) + 1 })}
                    isDisabled={isFunctionEnabled('lock') ? !!item.locked_at : false}
                    aria-label="数量を増やす"
                  >
                    +
                  </Button>
                </Flex>
              )}
            {isFixedList && item.locked_at && isFunctionEnabled('lock') && (
                <Box p={2} mt={2} bg="gray.100" borderRadius="md" borderWidth="1px" borderColor="gray.100">
                  <Text fontSize="sm" color="gray.700" fontWeight="bold">
                    ロック日時: {item.locked_at ? dayjs(item.locked_at).tz('Asia/Tokyo').format('YYYY/MM/DD HH:mm:ss') : ''}
                  </Text>
                  {item.signature_url && (
                    <Image src={item.signature_url} alt="サイン" boxSize="120px" mt={1} borderRadius="md" bg="white" />
                  )}
                </Box>
              )}


          </Box>
        </GridItem>
        );
      })}
    </Grid>

    <SignaturePadModal
      isOpen={isSigModalOpen && isFunctionEnabled('lock')}
      onClose={closeLockModal}
      onSave={handleLock}
    />

      <UserEquipmentModal
          isOpen={isUserModalOpen}
          onClose={onUserModalClose}
      onRegister={handleRegisterEquipment}
          config={config}
          targetUserId={targetUserId}
          isTsubo={isTsubo}
      />

    <MemoModal
      isOpen={isMemoModalOpen}
      onClose={closeMemoModal}
      memo={memoTarget?.memo}
      onSave={handleMemoSave}
      isLocked={isFunctionEnabled('lock') ? !!memoTarget?.locked_at : false}
      preferenceId={memoTarget?.id}
      materialFiles={memoTarget?.material_files}
      itemType={memoTarget?.item_type}
      onUploadMaterial={async (preferenceId, file) => {
        const user = auth.currentUser;
        if (!user) throw new Error('未ログインです');
        const data = await uploadUserItemPreferenceMaterial(
          memoTarget.item_type,
          memoTarget.item_id,
          file,
          user,
          targetUserId
        );
        setMemoTarget(prev => ({
          ...prev,
          material_files: data.material_files
        }));
      }}
      onReloadMaterialFiles={onReloadMaterialFiles}
      onPreferenceChange={onPreferenceChange}
      itemId={memoTarget?.item_id}
    />

    <Modal isOpen={isUnlockModalOpen && isFunctionEnabled('lock')} onClose={closeUnlockModal} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>ロック解除確認</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>このアイテムのロックを解除しますか？</Text>
        </ModalBody>
        <ModalFooter>
          <Button onClick={closeUnlockModal} mr={3}>キャンセル</Button>
          <Button bg="red.600" color="white" onClick={handleUnlock} isLoading={loadingUnlock}>OK</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
    </>
  );
} 